import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Filter, Sparkles } from 'lucide-react';
import SearchBar from './SearchBar';
import TableSearchBar from './TableSearchBar';
import { Modal } from './Modal';
import { FeatureGate } from '../../contexts/FeatureFlagContext';
import { aiAPI } from '../../services/api';
import toast from '../../services/toast';
import { cn, table as tableStyles } from './uiTheme';

/**
 * Client-side table with search, optional filter modal (column filters), sortable headers, and pagination.
 * Sorting and filtering apply only to rows in `data` (e.g. first page from API).
 *
 * @param {object} props
 * @param {any[]} props.data
 * @param {import('@tanstack/react-table').ColumnDef[]} props.columns
 * @param {(row: any) => string} [props.getRowId]
 * @param {boolean} props.isLoading
 * @param {string} props.emptyMessage
 * @param {string} [props.searchPlaceholder]
 * @param {import('@tanstack/react-table').SortingState} [props.initialSorting]
 * @param {number} [props.pageSize]
 * @param {boolean} [props.enablePagination]
 * @param {boolean} [props.showColumnHeaders=true] When false, the table `<thead>` (column titles / sort controls) is omitted.
 * @param {string} [props.globalFilter] When set with `onGlobalFilterChange`, search is controlled by the parent (e.g. header row).
 * @param {(value: string) => void} [props.onGlobalFilterChange]
 * @param {boolean} [props.hideTopSearchRow=false] When true, omits the top toolbar row that contains the main SearchBar (use with controlled search elsewhere).
 * @param {import('@tanstack/react-table').ColumnFiltersState} [props.columnFilters] When set with `onColumnFiltersChange`, column filters are controlled by the parent (e.g. filter modal in the page header).
 * @param {import('@tanstack/react-table').OnChangeFn<import('@tanstack/react-table').ColumnFiltersState>} [props.onColumnFiltersChange]
 * @param {import('@tanstack/react-table').SortingState} [props.sorting] When set with `onSortingChange`, sorting is controlled by the parent (e.g. commits tab sort menu).
 * @param {import('@tanstack/react-table').OnChangeFn<import('@tanstack/react-table').SortingState>} [props.onSortingChange]
 * @param {(args: { close: () => void, columnFilters: import('@tanstack/react-table').ColumnFiltersState, setColumnFilters: import('@tanstack/react-table').OnChangeFn<import('@tanstack/react-table').ColumnFiltersState> }) => React.ReactNode} [props.renderFilterPanel]
 * @param {boolean} [props.enableAiFilter=false] When true, shows the AI filter control.
 * @param {(row: any) => void} [props.onRowClick] When set, rows are clickable (keyboard: Enter/Space).
 * @param {(row: any) => string} [props.getRowAriaLabel] Accessible name for clickable rows.
 */
function FilterableDataTable({
  data,
  columns,
  getRowId,
  isLoading,
  emptyMessage,
  searchPlaceholder = 'Search…',
  initialSorting = [],
  pageSize = 25,
  enablePagination = true,
  showColumnHeaders = true,
  globalFilter: globalFilterProp,
  onGlobalFilterChange: onGlobalFilterChangeProp,
  hideTopSearchRow = false,
  columnFilters: columnFiltersProp,
  onColumnFiltersChange: onColumnFiltersChangeProp,
  sorting: sortingProp,
  onSortingChange: onSortingChangeProp,
  renderFilterPanel,
  enableAiFilter = false,
  onRowClick,
  getRowAriaLabel,
}: any) {
  const [internalSorting, setInternalSorting] = useState(initialSorting);
  const sortingControlled = sortingProp !== undefined;
  const sorting = sortingControlled ? sortingProp : internalSorting;

  const setSorting = useCallback(
    (updater) => {
      if (sortingControlled) {
        const prev = sortingProp ?? [];
        const next = typeof updater === 'function' ? updater(prev) : updater;
        onSortingChangeProp?.(next);
      } else {
        setInternalSorting(updater);
      }
    },
    [sortingControlled, sortingProp, onSortingChangeProp]
  );
  const [localGlobalFilter, setLocalGlobalFilter] = useState('');
  const searchControlled = globalFilterProp !== undefined;
  const globalFilter = searchControlled ? String(globalFilterProp ?? '') : localGlobalFilter;

  const handleGlobalFilterChange = useCallback(
    (updater) => {
      const prev = searchControlled ? String(globalFilterProp ?? '') : localGlobalFilter;
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const str = next == null ? '' : String(next);
      if (searchControlled) {
        onGlobalFilterChangeProp?.(str);
      } else {
        setLocalGlobalFilter(str);
      }
    },
    [searchControlled, globalFilterProp, localGlobalFilter, onGlobalFilterChangeProp]
  );

  const [internalColumnFilters, setInternalColumnFilters] = useState(
    /** @type {import('@tanstack/react-table').ColumnFiltersState} */ ([])
  );
  const columnFiltersControlled = columnFiltersProp !== undefined;
  const columnFilters = columnFiltersControlled ? columnFiltersProp : internalColumnFilters;

  const setColumnFilters = useCallback(
    (updater) => {
      if (columnFiltersControlled) {
        const prev = columnFiltersProp ?? [];
        const next = typeof updater === 'function' ? updater(prev) : updater;
        onColumnFiltersChangeProp?.(next);
      } else {
        setInternalColumnFilters(updater);
      }
    },
    [columnFiltersControlled, columnFiltersProp, onColumnFiltersChangeProp]
  );
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });
  useEffect(() => {
    setPagination((prev) => (prev.pageSize === pageSize ? prev : { ...prev, pageSize }));
  }, [pageSize]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [pageInput, setPageInput] = useState('');
  const pageInputId = useId();
  const [aiFilterMode, setAiFilterMode] = useState(false);
  const [aiFilterQuery, setAiFilterQuery] = useState('');
  const [aiFilterLoading, setAiFilterLoading] = useState(false);

  const tableColumnKeys = useMemo(
    () =>
      columns
        .map((col) => col.id ?? (typeof col.accessorKey === 'string' ? col.accessorKey : null))
        .filter(Boolean),
    [columns]
  );

  const columnVisibility = useMemo(() => {
    const vis = {};
    columns.forEach((col) => {
      if (col.meta?.filterOnly) {
        const cid = col.id ?? (typeof col.accessorKey === 'string' ? col.accessorKey : null);
        if (cid) vis[cid] = false;
      }
    });
    return vis;
  }, [columns]);

  const tableInst = useReactTable({
    data,
    columns,
    getRowId,
    state: { sorting, globalFilter, columnFilters, pagination },
    onSortingChange: setSorting,
    onGlobalFilterChange: handleGlobalFilterChange,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(enablePagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    autoResetPageIndex: true,
    initialState: {
      columnVisibility,
    },
  });

  const clearAllFilters = useCallback(() => {
    handleGlobalFilterChange('');
    setColumnFilters([]);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [handleGlobalFilterChange, setColumnFilters]);

  const hasActiveFilters = Boolean(globalFilter) || columnFilters.length > 0;

  const handleToolbarSearchChange = useCallback(
    (v) => {
      const next = typeof v === 'string' ? v : (v?.target?.value ?? '');
      handleGlobalFilterChange(next);
    },
    [handleGlobalFilterChange]
  );

  const filteredCount = tableInst.getFilteredRowModel().rows.length;
  const pageCount = tableInst.getPageCount();
  const pageIndex = tableInst.getState().pagination.pageIndex;
  const currentPageSize = tableInst.getState().pagination.pageSize;
  const rangeStart = filteredCount === 0 ? 0 : pageIndex * currentPageSize + 1;
  const rangeEnd = Math.min((pageIndex + 1) * currentPageSize, filteredCount);
  const showPagination = enablePagination && !isLoading && filteredCount > 0 && pageCount > 1;

  const closeFilterModal = useCallback(() => setFilterOpen(false), []);

  const handleAiFilter = useCallback(async () => {
    const q = aiFilterQuery.trim();
    if (!q || aiFilterLoading) return;
    setAiFilterLoading(true);
    try {
      const res = await aiAPI.chat({
        messages: [{ role: 'user', content: q }],
        systemContext: 'filterMode',
        tableColumns: tableColumnKeys,
      });
      const raw = typeof res === 'string' ? res : res.reply || '';
      const jsonText = raw
        .replace(/^```(?:json)?\s*/i, '')
        .replace(/\s*```$/i, '')
        .trim();
      const parsed = JSON.parse(jsonText);
      if (parsed.globalFilter != null) {
        handleGlobalFilterChange(String(parsed.globalFilter));
      }
      if (Array.isArray(parsed.columnFilters) && parsed.columnFilters.length) {
        setColumnFilters(parsed.columnFilters);
      }
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    } catch (err) {
      toast.error((err as Error).message || 'AI filter could not be applied');
    } finally {
      setAiFilterLoading(false);
    }
  }, [aiFilterQuery, aiFilterLoading, tableColumnKeys, handleGlobalFilterChange, setColumnFilters]);

  if (isLoading) {
    return <p className="p-4 text-sm text-slate-400">Loading…</p>;
  }

  if (data.length === 0) {
    return <p className="p-4 text-sm text-slate-400">{emptyMessage}</p>;
  }

  const showToolbarRow =
    !hideTopSearchRow ||
    typeof renderFilterPanel === 'function' ||
    (enableAiFilter && aiFilterMode);

  return (
    <>
      {showToolbarRow && (
        <div className="px-4 py-3 border-b border-dark-border flex flex-wrap items-center gap-3">
          {!hideTopSearchRow && (
            <div className="flex-1 min-w-[200px] max-w-md">
              <SearchBar
                value={globalFilter ?? ''}
                onChange={handleToolbarSearchChange}
                placeholder={searchPlaceholder}
              />
            </div>
          )}
          {typeof renderFilterPanel === 'function' && (
            <button
              type="button"
              onClick={() => setFilterOpen(true)}
              className="btn-secondary-mdl-sm inline-flex items-center gap-2"
              aria-label="Open column filters"
              aria-haspopup="dialog"
            >
              <Filter className="w-4 h-4" aria-hidden />
              Filters
            </button>
          )}
          {enableAiFilter ? (
            <FeatureGate flag="ui.ai.chat">
              <button
                type="button"
                onClick={() => setAiFilterMode((v) => !v)}
                className={cn(
                  'inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
                  aiFilterMode
                    ? 'border-brand-teal/40 bg-brand-teal/10 text-brand-teal-light'
                    : 'btn-secondary-mdl-sm'
                )}
                aria-pressed={aiFilterMode}
              >
                <Sparkles className="w-4 h-4" aria-hidden />
                AI filter
              </button>
            </FeatureGate>
          ) : null}
        </div>
      )}
      {enableAiFilter && aiFilterMode && (
        <FeatureGate flag="ui.ai.chat">
          <div className="px-4 pb-3 flex flex-wrap items-center gap-2 border-b border-dark-border">
            <input
              type="text"
              value={aiFilterQuery}
              onChange={(e) => setAiFilterQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAiFilter()}
              placeholder="Describe how to filter this table…"
              className="input-mdl flex-1 min-w-[200px] text-sm py-2"
              disabled={aiFilterLoading}
            />
            <button
              type="button"
              onClick={handleAiFilter}
              disabled={aiFilterLoading || !aiFilterQuery.trim()}
              className="btn-primary-mdl inline-flex items-center gap-2 text-sm py-2"
            >
              <Sparkles className="w-4 h-4" aria-hidden />
              {aiFilterLoading ? 'Applying…' : 'Apply'}
            </button>
          </div>
        </FeatureGate>
      )}

      {!hideTopSearchRow && (
        <div className="px-4 pt-3">
          <TableSearchBar
            value={globalFilter ?? ''}
            onChange={handleToolbarSearchChange}
            placeholder={searchPlaceholder}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearAllFilters}
            resultCount={filteredCount}
            hideSearchBar
          />
        </div>
      )}

      {typeof renderFilterPanel === 'function' && (
        <Modal
          isOpen={filterOpen}
          onClose={closeFilterModal}
          title="Filters"
          icon={<Filter className="w-5 h-5 text-brand-purple" />}
          contentClassName="max-w-md"
          bodyClassName="px-6 pb-6 pt-0"
        >
          {renderFilterPanel({
            close: closeFilterModal,
            columnFilters,
            setColumnFilters,
          })}
        </Modal>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-dark-border">
          {showColumnHeaders ? (
            <thead className={tableStyles.thead}>
              {tableInst.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    if (!header.column.getIsVisible()) return null;
                    return (
                      <th key={header.id} className={tableStyles.th}>
                        <div className="space-y-2">
                          <div
                            role={header.column.getCanSort() ? 'button' : undefined}
                            tabIndex={header.column.getCanSort() ? 0 : undefined}
                            className={`flex items-center justify-between group ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                            onClick={header.column.getToggleSortingHandler()}
                            onKeyDown={(e) => {
                              if (!header.column.getCanSort()) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                header.column.getToggleSortingHandler()?.(e);
                              }
                            }}
                          >
                            <span className="font-medium">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                            {header.column.getCanSort() && (
                              <div className="ml-2 opacity-60 group-hover:opacity-100 transition-opacity">
                                {header.column.getIsSorted() === 'asc' && (
                                  <ArrowUp className="w-4 h-4 text-brand-purple" aria-hidden />
                                )}
                                {header.column.getIsSorted() === 'desc' && (
                                  <ArrowDown className="w-4 h-4 text-brand-purple" aria-hidden />
                                )}
                                {!header.column.getIsSorted() && (
                                  <ArrowUpDown className="w-4 h-4 text-slate-500" aria-hidden />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
          ) : null}
          <tbody className="bg-dark-surface divide-y divide-dark-border">
            {tableInst.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={Math.max(1, tableInst.getVisibleLeafColumns().length)}
                  className="px-4 py-8 text-center text-sm text-slate-400"
                >
                  No rows match your search or filters.
                </td>
              </tr>
            ) : (
              tableInst.getRowModel().rows.map((row) => {
                const rowData = row.original;
                const clickable = typeof onRowClick === 'function';
                return (
                <tr
                  key={row.id}
                  tabIndex={clickable ? 0 : undefined}
                  aria-label={
                    clickable && getRowAriaLabel ? getRowAriaLabel(rowData) : undefined
                  }
                  className={cn(
                    tableStyles.rowHover,
                    clickable &&
                      'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-vivid-red/40'
                  )}
                  onClick={clickable ? () => onRowClick(rowData) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return;
                          if (e.key === ' ') e.preventDefault();
                          onRowClick(rowData);
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => {
                    const cellClass = (cell.column.columnDef.meta as any)?.tdClassName ?? tableStyles.td;
                    return (
                      <td key={cell.id} className={cellClass}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="border-t border-dark-border bg-dark-elevated/50 px-4 py-3 sm:px-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-400">
              Showing{' '}
              <span className="font-medium text-slate-200">{rangeStart}</span>
              {' – '}
              <span className="font-medium text-slate-200">{rangeEnd}</span>
              {' of '}
              <span className="font-medium text-slate-200">{filteredCount}</span>
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end">
              <div className="flex items-center gap-2 text-sm">
                <label htmlFor={pageInputId} className="text-slate-500">
                  Page
                </label>
                <input
                  id={pageInputId}
                  type="number"
                  min={1}
                  max={pageCount}
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    const page = pageInput ? Number(pageInput) - 1 : 0;
                    if (page >= 0 && page < pageCount) {
                      tableInst.setPageIndex(page);
                      setPageInput('');
                    }
                  }}
                  placeholder={String(pageIndex + 1)}
                  className="input-mdl-sm w-14 py-1 text-center"
                  aria-label="Go to page"
                />
                <span className="text-slate-500">of {pageCount}</span>
              </div>
              <nav
                className="inline-flex overflow-hidden rounded-lg border border-dark-border"
                aria-label="Pagination"
              >
                <button
                  type="button"
                  onClick={() => tableInst.previousPage()}
                  disabled={!tableInst.getCanPreviousPage()}
                  className="btn-secondary-mdl-sm inline-flex items-center gap-1.5 rounded-none border-0 border-r border-dark-border px-3 disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="hidden sm:inline">Previous</span>
                </button>
                <button
                  type="button"
                  onClick={() => tableInst.nextPage()}
                  disabled={!tableInst.getCanNextPage()}
                  className="btn-secondary-mdl-sm inline-flex items-center gap-1.5 rounded-none border-0 px-3 disabled:opacity-40"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FilterableDataTable;
